const SLICE_NAME = 'agentModeSlice';

/**
 * HITL-1604: the one global mode that says what the supervising agent is allowed
 * to do while the AI runs the order.
 *
 * `Shadow` is **silent** — nothing the agent does in it reaches the customer or
 * the POS. `Intercept` is the only mode that can speak or send, and it is what
 * today's "agent has taken over" state becomes.
 *
 * Two values, deliberately. An earlier draft had a third, `Supervise`, sitting
 * before `Shadow` as a read-only mode in which the agent could not even build a
 * cart. It was cut before it ever shipped: the agent gains nothing from being
 * refused a scratch cart that never leaves the terminal, and the distinction
 * cost a mode the agent had to reason about mid-order. Shadow is now the
 * resting state, so the enum has no "watching but idle" value — watching IS
 * Shadow.
 *
 * Deliberately NOT a rename of `isAIAutoMode`, and nothing in this feature
 * writes to it: mutating that flag to fix a display symptom caused a production
 * incident (dropped orders + a cart revert).
 */
enum AgentMode {
  shadow = 'Shadow',
  intercept = 'Intercept',
}

/** Every HITL session starts here. There is no persistence — see the slice. */
const DEFAULT_AGENT_MODE = AgentMode.shadow;

/**
 * HITL-1650: who put the console in the mode it is currently in.
 *
 * `Intercept` is one-way for the session — but only when the AGENT chose it.
 * Once the console can arm `Intercept` on the AI's behalf, "we are in Intercept"
 * stops being one fact and becomes two, and the difference is the whole of
 * whether the agent may step back out:
 *
 *   enteredBy   how it happened                        may the agent leave?
 *   ──────────  ─────────────────────────────────────  ─────────────────────
 *   'agent'     they pressed Intercept, or Take over   no — their decision
 *   'system'    the AI asked for a human and the       yes — not their
 *               console armed it for them                   decision yet
 *
 * A one-way lock on a mode the agent never chose is the console overruling
 * them, which is the opposite of what arming it is for: the point is to have
 * the gates already open when they look, not to commit them to a takeover
 * because the AI escalated while they were reading the transcript.
 *
 * Same two words as the wire's `AgentModeChangeTrigger`, deliberately, so the
 * state and the metric answer "who" the same way. They stay separate types: the
 * trigger is a frozen event contract and this is internal state, and the slice
 * must not import from `messagingSlice` to get one.
 */
type AgentModeEnteredBy = 'agent' | 'system';

/** Nobody has been overruled yet. */
const DEFAULT_ENTERED_BY: AgentModeEnteredBy = 'agent';

export {
  SLICE_NAME,
  AgentMode,
  DEFAULT_AGENT_MODE,
  DEFAULT_ENTERED_BY,
};
export type { AgentModeEnteredBy };
